import React, { useCallback, useMemo } from 'react'
import { ArrayParam, NumberParam, StringParam, useQueryParams, withDefault } from 'use-query-params'

import { useDidMountEffect, useQueryList } from '@percent/admin-dashboard/common/hooks'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { OrganisationTypes } from '@percent/domain/verifications'
import { OrganizationsTable } from './organizationsTable/OrganizationsTable'

const PAGE_SIZE = 25
const validOrgTypes = new Set(Object.values(OrganisationTypes))

export function OrganizationsContainer() {
  const { adminService } = useServices()

  const [queryParams, setQueryParams] = useQueryParams({
    query: StringParam,
    page: withDefault(NumberParam, 1),
    type: ArrayParam
  })

  const listParams = useMemo(
    () => ({
      ...queryParams,
      onlyActive: false,
      page: queryParams.page || 1,
      type: queryParams.type?.length
        ? queryParams.type.filter((t): t is OrganisationTypes => validOrgTypes.has(t as OrganisationTypes))
        : [...validOrgTypes]
    }),
    [queryParams.page, queryParams.query, queryParams.type]
  )

  const [{ totalResults, isLoading, dataOrNull, errorMessageOrNull }, { query, refresh }] = useQueryList(
    adminService.getOrganisations,
    listParams
  )

  const handleNextPage = useCallback(() => {
    setQueryParams({
      query: queryParams.query,
      page: queryParams?.page ? queryParams.page + 1 : 1
    })
  }, [queryParams.page, queryParams.query, setQueryParams])

  const handlePreviousPage = useCallback(() => {
    setQueryParams({
      query: queryParams.query,
      page: queryParams?.page && queryParams.page > 1 ? queryParams.page - 1 : 1
    })
  }, [queryParams.page, queryParams.query, setQueryParams])

  useDidMountEffect(() => query(listParams), [listParams, query])

  return (
    <OrganizationsTable
      organisations={dataOrNull}
      totalResults={totalResults}
      nextPage={(queryParams?.page || 1) < totalResults / PAGE_SIZE ? handleNextPage : null}
      previousPage={(queryParams?.page || 1) > 1 ? handlePreviousPage : null}
      isLoading={isLoading}
      errorMessage={errorMessageOrNull}
      queryParams={queryParams}
      setQueryParams={setQueryParams}
      refresh={refresh}
    />
  )
}
